import queryString from 'query-string';
import { Stringifiable } from './utilityTypes';
import { generatePath } from './router';
import { join } from './strings';
import { APP_BASE_URL } from '../config';
import { useLocation } from 'react-router-dom';
import { parseTargetPlan, parseFeature } from './onboarding';
import { TargetPlan } from '../constants/onboarding';

export type UrlParams = Record<string, string | number | boolean | undefined>;

export const getQueryParam = (name: string): string | undefined =>
    new URL(window.location.href).searchParams.get(name) || undefined;

export const getQueryParams = <S extends string>(
    ...params: S[]
): Record<S, string | undefined> => {
    return params.reduce(
        (obj, param) => ({ ...obj, [param]: getQueryParam(param) }),
        {}
    ) as Record<S, string | undefined>;
};

export const isAbsoluteUrl = (url: string): boolean => {
    return /^(?:[a-z]+:)?\/\//i.test(url);
};

export const addQueryParamsToUrl = (
    url: string,
    newParams: Record<string, string>,
    baseUrl = APP_BASE_URL
): string => {
    let urlObj: URL;
    if (isAbsoluteUrl(url)) {
        urlObj = new URL(url);
    } else {
        urlObj = new URL(url, baseUrl);
    }
    Object.entries(newParams).forEach(([k, v]) => {
        urlObj.searchParams.set(k, v);
    });
    return urlObj.toString();
};

export const renderUrl = (
    urlTemplate: string,
    urlParams: UrlParams = {},
    queryParams: Record<string, Stringifiable | undefined> = {}
): string => {
    const params = Object.entries(queryParams).reduce((obj, [k, v]) => {
        if (v !== undefined && v !== null) {
            return { ...obj, [k]: v };
        }
        return obj;
    }, {});
    const query = queryString.stringify(params);
    const url = generatePath(urlTemplate, urlParams);
    return join([url, query], url.includes('?') ? '&' : '?');
};

export const redirect = (to: string): void => {
    window.location.href = to;
};

export type LoginQueryParams = {
    redir?: string;
    feature?: string;
    targetPlan?: TargetPlan;
};

export function useLoginQueryParams(): LoginQueryParams {
    const location = useLocation();
    const parsedQuery = queryString.parse(location.search);

    return {
        ...(typeof parsedQuery.redir === 'string'
            ? {
                  redir: parsedQuery.redir,
              }
            : {}),
        ...(typeof parsedQuery.feature === 'string'
            ? {
                  feature: parseFeature(parsedQuery.feature),
              }
            : {}),
        ...(typeof parsedQuery.targetPlan === 'string'
            ? {
                  targetPlan: parseTargetPlan(parsedQuery.targetPlan),
              }
            : {}),
    };
}

export type RegisterQueryParams = {
    inviteCode?: string;
    targetPlan?: TargetPlan;
};

export function useRegisterQueryParams(): RegisterQueryParams {
    const location = useLocation();
    const parsedQuery = queryString.parse(location.search);
    return {
        ...(typeof parsedQuery.inviteCode === 'string'
            ? {
                  inviteCode: parsedQuery.inviteCode,
              }
            : {}),
        ...(typeof parsedQuery.targetPlan === 'string'
            ? {
                  targetPlan: parseTargetPlan(parsedQuery.targetPlan),
              }
            : {}),
    };
}

export const getRedirUrl = (defaultRedir: string): string => {
    const params = useLoginQueryParams();
    return params.redir ?? defaultRedir;
};
